/**
 * What passes between the page and the bot's worker.
 *
 * A request carries a variant and a move log, not a position. That is the same rule the
 * server plays by — a match *is* its move log — and it has the same payoff here: the
 * worker cannot end up holding a board that has quietly diverged from the one on screen,
 * because it does not hold a board at all between requests.
 *
 * Everything crosses by structured clone, so these are plain data. No typed arrays, no
 * classes, nothing with a method on it.
 */

import type { Move, Variant } from '@gok/engine';
import type { LevelId } from '@gok/bot';

export interface BotRequest {
  /** Echoed back, so a reply that arrives after the game moved on can be dropped. */
  readonly id: number;
  readonly variant: Variant;
  readonly moves: readonly Move[];
  readonly level: LevelId;
}

export interface BotReply {
  readonly id: number;
  readonly move: Move;
  /** Centipawns, from the bot's point of view. */
  readonly score: number;
  readonly depth: number;
  readonly nodes: number;
  readonly elapsedMs: number;
  readonly mate: boolean;
}

export interface BotFailure {
  readonly id: number;
  readonly error: string;
}

export type BotMessage = BotReply | BotFailure;

export const isFailure = (message: BotMessage): message is BotFailure =>
  'error' in message;
